import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import EveChildFigure from "./EveChildFigure";

const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const EpiloguePlayer = () => {
    const audioRef = useRef(null);
    const barRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);

    useEffect(() => {
        const audio = new Audio("/assets/epilogue.mp3");
        audio.preload = "metadata";
        audioRef.current = audio;

        const onTime = () => setProgress(audio.currentTime);
        const onMeta = () => setDuration(audio.duration);
        const onEnd = () => setPlaying(false);
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);

        return () => {
            audio.pause();
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
        };
    }, []);

    const toggle = () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (playing) {
            audio.pause();
            setPlaying(false);
        } else {
            audio.play();
            setPlaying(true);
        }
    };

    const seek = (e) => {
        const audio = audioRef.current;
        const bar = barRef.current;
        if (!audio || !bar || !duration) return;
        const rect = bar.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        audio.currentTime = ratio * duration;
        setProgress(audio.currentTime);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mx-auto mt-10 flex max-w-md items-center gap-4 rounded-sm border border-[rgba(212,175,55,0.3)] bg-[rgba(15,35,24,0.6)] p-4 backdrop-blur-md"
            data-testid="epilogue-player"
        >
            <button
                onClick={toggle}
                data-testid="epilogue-play-button"
                aria-label={playing ? "Pause the epilogue" : "Play the epilogue"}
                className="btn-eden flex h-12 w-12 flex-none items-center justify-center rounded-full bg-gradient-to-br from-[rgba(160,120,60,0.95)] to-[rgba(120,85,40,0.95)] text-[#f5e6c8]"
            >
                {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <p className="font-cinzel text-left text-[0.55rem] tracking-[0.3em] text-[#d4af37] uppercase">
                        Read Aloud — The Epilogue
                    </p>
                    <div className={`eq ${playing ? "eq-playing" : ""}`} aria-hidden="true">
                        <span className="eq-bar" />
                        <span className="eq-bar" />
                        <span className="eq-bar" />
                        <span className="eq-bar" />
                        <span className="eq-bar" />
                    </div>
                </div>
                <div
                    ref={barRef}
                    onClick={seek}
                    data-testid="epilogue-progress-bar"
                    className="mt-3 h-1.5 w-full cursor-pointer rounded-full bg-[rgba(212,175,55,0.18)]"
                >
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-[#b89860] to-[#f3e5ab] transition-[width] duration-200"
                        style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }}
                    />
                </div>
                <div className="font-cormorant mt-1.5 flex justify-between text-xs italic text-[#8a7a60]">
                    <span data-testid="epilogue-current-time">{fmt(progress)}</span>
                    <span data-testid="epilogue-duration">{fmt(duration)}</span>
                </div>
            </div>
        </motion.div>
    );
};

const STANZAS = [
    [
        "Eve does not physically return.",
        "Her lesson does.",
    ],
    [
        "She returns whenever a father lets his son cry.",
        "Whenever a man admits he is afraid.",
        "Whenever someone is rejected but refuses to become cruel.",
        "Whenever a broken heart refuses to become a closed heart.",
        "Whenever strength protects rather than controls.",
        "Whenever someone asks:",
        "\u201CWhat hurts?\u201D",
        "Whenever someone chooses to love while knowing love carries risk.",
    ],
    [
        "At the beginning:",
        "Eve Came First meant Eve existed before Adam.",
        "At the end, it means something more.",
    ],
    [
        "Love came before fear.",
        "Compassion came before domination.",
        "Feeling came before the wall.",
        "The rose came before the grave.",
        "And what came first was never completely lost.",
    ],
];

export default function EpilogueSection() {
    return (
        <section id="epilogue" data-testid="epilogue-section" className="relative mx-auto max-w-3xl px-6 py-32 text-center sm:py-44">
            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1 }}
                className="font-cinzel text-xs tracking-[0.45em] text-[#b89860] uppercase"
            >
                The Epilogue
            </motion.p>

            <EpiloguePlayer />

            <div className="mt-16 space-y-12">
                {STANZAS.map((stanza, i) => (
                    <motion.div
                        key={i}
                        data-testid={`epilogue-stanza-${i + 1}`}
                        initial={{ opacity: 0, y: 34 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="space-y-4"
                    >
                        {stanza.map((line, j) => (
                            <p key={j} className="font-cormorant text-lg italic leading-relaxed text-[#d4c4a0] sm:text-xl">
                                {line}
                            </p>
                        ))}
                    </motion.div>
                ))}
            </div>

            <EveChildFigure className="mt-24" testid="epilogue-eve-child-figure" />

            <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                className="mt-24"
                data-testid="epilogue-finale"
            >
                <div className="gold-rule mx-auto mb-16 w-32" />
                <p className="font-cinzel text-3xl leading-snug text-[#f0d9a8] [text-shadow:0_0_30px_rgba(255,200,100,0.3)] sm:text-5xl">
                    Eve came first.
                </p>
                <p className="font-cinzel mt-6 text-xl tracking-wide text-[#d4af37] sm:text-3xl">
                    And she is coming back.
                </p>
                <p className="font-cinzel mt-14 text-[0.65rem] tracking-[0.5em] text-[#8a7a60] uppercase">
                    — The End —
                </p>
            </motion.div>
        </section>
    );
}
